import FileSaver from 'file-saver';
import { formatTimestampAsDate, formatTimestampAsTime } from '../utils/dates-utils';
import axios from 'axios';
import { headerConfig } from './utils';
import { FETCH_STANDARD_REPORTS, REQUEST_RESULT } from './types';
import { REPORTS_URL, transformFormValues } from './report-actions';
import {
  TIME_FROM_FIELD,
  TIME_TO_FIELD,
  TOP_SALES_QUANTITY,
} from '../components/pages/reports/standard/StandardReportUtils';
import { analyticEvent } from '../analytics';

export function downloadTransactionFile(reportName, values, machinesOptions, type, setDownloadProgress, downloadTransactionFileForSie4, downloadTransactionFileForExcel, setDownloading) {
  const urlType = type === 'excel' ? 'excel' : 'sie4';
  
  if (urlType === 'sie4') {
    return downloadTransactionFileForSie4(reportName, values, machinesOptions, setDownloadProgress, setDownloading);
  } else {
    return downloadTransactionFileForExcel(reportName, values, setDownloadProgress, setDownloading);
  }
}

export const downloadTransactionFileForSie4 = (reportName, values, machinesOptions, setDownloadProgress, setDownloading) => {
  const { machineIDs, ...formValues } = values;
  const [dateFrom, dateTo] = [formatTimestampAsDate(formValues.dateFrom), formatTimestampAsDate(formValues.dateTo)];
  const urlType = 'sie4';
  
  return dispatch => {
    const machinesMapped = machineIDs[0] === null ? machinesOptions.map(option => option.value) : machineIDs;
    
    const axiosRequests = machinesMapped.map(machineId => {
      const requestUrl = `${REPORTS_URL}/download-z-report-by-date/${urlType}/?machineId=${machineId}&startDate=${dateFrom}&endDate=${dateTo}`;
  
      return axios.get(
        requestUrl,
        {
          ...dispatch(headerConfig()),
          responseType: 'arraybuffer',
          onDownloadProgress: progressEvent => {
            setDownloading(true);
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setDownloadProgress(progress);
          },
        },
      )
        .then(response => {
          analyticEvent('SIE4', 'Z-Report Download', 'Success', { machineId });
          createBlobAndDownload(response, reportName, urlType, 'se', machineId, dateFrom, dateTo);
        })
        .catch(error => {
          analyticEvent('SIE4', 'Z-Report Download', 'Failure', { machineId });
          dispatch({
            type: REQUEST_RESULT,
            payload: { message: `Download error: ${error.response?.status || 'Unknown'}` },
          });
        });
    });
  
    return Promise.all(axiosRequests)
      .then(() => setDownloading(true))
      .catch(() => setDownloading(true))
      .finally(() => setDownloading(false));
  };
};

export const getTimeParametersForServer = (timeFrom, timeTo) => {
  return [
    { Name: TIME_FROM_FIELD.server, Value: timeFrom },
    { Name: TIME_TO_FIELD.server, Value: timeTo },
  ];
};

export function fetchSie4Reports(reportName, machineId, startDate, endDate, form, setLoadingProgress) {
  let transformedResponse = [];
  setLoadingProgress(0);
  return dispatch => {
    axios.get(`${REPORTS_URL}/download-z-report-by-date/json?machineId=${machineId}&startDate=${startDate}&endDate=${endDate}`, dispatch(headerConfig()))
      .then(response => {
        transformedResponse = response.data.ZReports;
      })
      .then(() => {
          setLoadingProgress(100);
          form && form.setSubmitting(false);
          dispatch({
            type: FETCH_STANDARD_REPORTS,
            payload: transformedResponse,
          });
        },
      )
      .catch(error => {
        setLoadingProgress(100);
        form && form.setSubmitting(false);
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: error.response.data },
        });
      });
  };
}

export function downloadSie4File(machineId, machineName, startDate, endDate, setLoadingProgress) {
  setLoadingProgress(0);
  const endpoint = `${REPORTS_URL}/download-z-report-by-date/sie4?machineId=${machineId}&startDate=${startDate}&endDate=${endDate}`;
  return (dispatch) => {
    axios.get(endpoint, {
      ...dispatch(headerConfig()),
      responseType: 'arraybuffer',
      onDownloadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        setLoadingProgress(progress);
      },
    })
      .then((response) => {
        const blob = new Blob([response.data], { type: 'application/octet-stream' });
        const fileName = `SIE4_Export_${machineName}_${startDate}_${endDate}.se`;
        FileSaver.saveAs(blob, fileName);
        setLoadingProgress(100);
      })
      .catch((error) => {
        const decoder = new TextDecoder('utf-8');
        const errorMessage = decoder.decode(error.response.data);
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: errorMessage },
        });
        setLoadingProgress(100);
      });
  };
}

export function downloadExcelFile(machineId, machineName, startDate, endDate, setLoadingProgress) {
  setLoadingProgress(0);
  const endpoint = `${REPORTS_URL}/download-z-report-by-date/xls?machineId=${machineId}&startDate=${startDate}&endDate=${endDate}`;
  return (dispatch) => {
    axios.get(endpoint, {
      ...dispatch(headerConfig()),
      responseType: 'arraybuffer',
      onDownloadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        setLoadingProgress(progress);
      },
    })
      .then((response) => {
        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
        const fileName = `SIE4_Export-Report_${machineName}_${startDate}_${endDate}.xls`;
        FileSaver.saveAs(blob, fileName);
        setLoadingProgress(100);
      })
      .catch((error) => {
        const decoder = new TextDecoder('utf-8');
        const errorMessage = decoder.decode(error.response.data);
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: errorMessage },
        });
        setLoadingProgress(100);
      });
  };
}

export const downloadTransactionFileForExcel = (reportName, values, setDownloadProgress, setDownloading) => {
  const { machineIDs, timeFrom, timeTo, dateFrom: rawDateFrom, dateTo: rawDateTo, ...formValues } = values;
  const [urlType, fileType] = ['excel', 'xlsx'];
  const dateFrom = formatTimestampAsDate(rawDateFrom);
  const dateTo = formatTimestampAsDate(rawDateTo);
  const timeParameters = (timeFrom && timeTo)
    ? getTimeParametersForServer(formatTimestampAsTime(timeFrom), formatTimestampAsTime(timeTo)) : [];
  const includeQuantity = ['TopSalesPerProduct', 'TopSalesPerCategory'].includes(reportName);
  const isValidValue = (key, value) => key !== 'dateRange' && value != null;
  const filteredFormValues = Object.fromEntries(
    Object.entries(formValues).filter(([key, value]) => isValidValue(key, value)),
  );
  
  const baseParams = {
    dateFrom,
    dateTo,
    ...filteredFormValues,
    ...(includeQuantity && { [TOP_SALES_QUANTITY.server]: formValues.topSalesQuantity }),
  };
  
  const dynamicFileNamePart = Object.entries(filteredFormValues)
    .filter(([key]) => !['dateFrom', 'dateTo', 'timeFrom', 'timeTo'].includes(key))
    .map(([key, value]) => `__${key}${value ? `_${value}` : ''}`)
    .join('');
  
  const baseParameters = [transformFormValues(baseParams), timeParameters].flat();
  const setDownloadingStatus = (status) => setDownloading && setDownloading(status);
  
  return dispatch => {
    const machineIdPart = machineIDs.length > 1 ? 'multiple-machines' : machineIDs[0] || 'all';
    const transformedFormValues = {
      MachineIds: machineIDs[0] === null ? undefined : machineIDs,
      Parameters: baseParameters,
    };
    setDownloadingStatus(true);
    axios.post(`${REPORTS_URL}/get-report-result/${reportName}/${urlType}`,
      transformedFormValues,
      {
        ...dispatch(headerConfig()),
        responseType: 'arraybuffer',
        onDownloadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setDownloadProgress && setDownloadProgress(progress);
        },
      })
      .then(response => {
        analyticEvent('SIE4 Excel', 'Excel Download', 'Success', { machineIDs: machineIDs });
        createBlobAndDownloadForExcel(response, reportName, urlType, fileType,
          dynamicFileNamePart,
          machineIdPart, dateFrom, dateTo,
        );
      })
      .catch(error => {
        analyticEvent('SIE4 Excel', 'Excel Download', 'Failure', { machineIDs: machineIDs });
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: `Download error: ${error.response ? error.response.status : 'Unknown error'}` },
        });
      })
      .finally(() => setDownloadingStatus(false));
  };
};

function createBlobAndDownload(response, reportName, urlType, fileType, machineId, dateFrom, dateTo) {
  const blob = new Blob([response.data], { type: `application/${fileType}` });
  const machineIdPart = machineId !== undefined ? `__${machineId}` : '';
  const fileName = `${reportName}_${urlType}${machineIdPart}__${dateFrom}-${dateTo}.${fileType}`;
  FileSaver.saveAs(blob, fileName);
  analyticEvent('ID', 'PeriodicReport Download', 'PDF Download', 'Failure');
}

function createBlobAndDownloadForExcel(response, reportName, urlType, fileType, dynamicPart, machineIdPart, dateFrom, dateTo) {
  const blob = new Blob([response.data], { type: `application/${fileType}` });
  const machineIdPartFormatted = machineIdPart ? `__${machineIdPart}` : '';
  const fileName = `${reportName}_${urlType}${dynamicPart}${machineIdPartFormatted}__${dateFrom}-${dateTo}.${fileType}`;
  FileSaver.saveAs(blob, fileName);
  analyticEvent('ID', 'PeriodicReport Download', 'PDF Download', 'Success');
}